import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import {
	GoogleMap,
	MapInfoWindow,
	MapMarker,
	MapMarkerClusterer,
} from '@angular/google-maps';
import { Subscription } from 'rxjs';
import { DataSetComponent } from './data-set.component';

@Component({
	selector: 'app-data-set-map-view',
	templateUrl: './data-set-map.component.html',
	styleUrls: ['./data-set.component.scss'],
	imports: [GoogleMap, MapMarkerClusterer, MapMarker, MapInfoWindow],
})
export class DataSetMapViewComponent implements OnInit, OnDestroy {
	@Input() markersInfo: MarkerInfo[] = [];
	@Input() mapCenter: google.maps.LatLngLiteral = {
		lat: DataSetMapViewComponent.SENTRILOCK_HQ_LAT,
		lng: DataSetMapViewComponent.SENTRILOCK_HQ_LNG,
	};

	@Input() isPropertyMarker: boolean = false;

	@Output() currentLocation: EventEmitter<GeolocationPosition> =
		new EventEmitter();

	@ViewChild('deviceGoogleMap', { static: false })
	map!: GoogleMap;
	@ViewChild(MapMarkerClusterer, { static: false })
	markerClusterer!: MapMarkerClusterer;
	@ViewChild(MapInfoWindow, { static: false })
	info!: MapInfoWindow;

	clusterInfoWindow = new google.maps.InfoWindow();

	static SENTRILOCK_HQ_LAT: any = 39.363522;
	static SENTRILOCK_HQ_LNG: any = -84.366302;

	mapOptions: google.maps.MapOptions = {
		zoom: 10,
		mapTypeControl: false,
		streetViewControl: false,
	};
	currentMapZoom = 0;
	displayed = false;

	private presentationSubscription?: Subscription;

	subscription?: Subscription;
	isMapSubInit = false;

	infoContent = '';

	clusterIndex = 0;
	decreaseClusterIndexMethodRef: any;
	increaseClusterIndexMethodRef: any;
	clusterInfoWindowDomReadyListener: google.maps.MapsEventListener | null =
		null;
	clusterInfoWindowCloseClickListener: google.maps.MapsEventListener | null =
		null;

	clusterOfMarkers: google.maps.Marker[] = [];
	infoWindowBackArrow: HTMLElement | null = null;
	infoWindowForwardArrow: HTMLElement | null = null;

	clusterInfoWindowContent: string = '';

	constructor(private dataSet: DataSetComponent) {
		this.presentationSubscription = this.dataSet.presentationMode.subscribe(
			(mode) => {
				this.displayed = mode === 'map';
			}
		);
	}

	ngOnInit(): void {
		this.setClusterInfoWindowContent();
		this.decreaseClusterIndexMethodRef = this.decreaseClusterIndex.bind(this);
		this.increaseClusterIndexMethodRef = this.increaseClusterIndex.bind(this);

		navigator.geolocation.getCurrentPosition((position) => {
			this.mapCenter = {
				lat: position.coords.latitude,
				lng: position.coords.longitude,
			};
			this.currentLocation.emit(position);
		});
	}

	removeClusterOfMarkerListeners(): void {
		this.infoWindowBackArrow?.removeEventListener(
			'click',
			this.decreaseClusterIndexMethodRef
		);
		this.infoWindowForwardArrow?.removeEventListener(
			'click',
			this.increaseClusterIndexMethodRef
		);
		this.clusterInfoWindowDomReadyListener?.remove();
		this.clusterInfoWindowCloseClickListener?.remove();
	}

	setClusterInfoWindowContent(): void {
		this.clusterInfoWindowContent =
			'<div style="display: flex; align-items: center;">' +
			'<button data-automation-id="map-back-button" id="backArrow" class="mat-icon-button" style="display: flex; align-items: center">' +
			'<span class="material-icons" style="color: #C2D4DF; font-weight: 1000; font-size: xx-large">keyboard_arrow_left</span>' +
			'</button>' +
			'<div>' +
			`<div id="propertyDeviceInfo"></div>` +
			'</div>' +
			'<button data-automation-id="map-forward-button" id="forwardArrow" class="mat-icon-button" style="display: flex; align-items: center; justify-content: end;">' +
			'<span class="material-icons" style="color: #C2D4DF; font-weight: 1000; font-size: xx-large">keyboard_arrow_right</span>' +
			'</button>' +
			'</div>';

		this.clusterInfoWindow.setContent(this.clusterInfoWindowContent);
	}

	openInfo(marker: MapMarker): void {
		this.infoContent = this.createInfoWindowContent(marker.getTitle()!);
		this.info.open(marker);
	}

	getMarkerInfoFromTitle(markerTitle: string): MarkerInfo {
		if (this.isPropertyMarker) {
			return this.markersInfo.find((marker) => {
				if (!!marker.address1) {
					return markerTitle.includes(marker.address1);
				}
				return false;
			})!;
		} else {
			const deviceSn = markerTitle.substr(
				markerTitle?.lastIndexOf('SN:') + 4,
				markerTitle.length - 1
			);
			return this.markersInfo.find((marker) => marker.deviceSn === deviceSn)!;
		}
	}

	createInfoWindowContent(markerTitle: string): string {
		const {
			locality,
			locationName,
			postalCode,
			address1,
			deviceSn,
			deviceId,
			locationId,
		} = this.getMarkerInfoFromTitle(markerTitle);

		let locName = locationName;
		let serialNum = deviceSn;
		const urlStart = window.location.href.slice(
			0,
			window.location.href.indexOf('/')
		);

		locName =
			'<a data-automation-id="property-details-link" class="device-map-home-inner-html-1" href= "' +
			urlStart +
			'property-details/' +
			locationId +
			'">' +
			locName +
			'</a>';

		let locAddress =
			'<span  class="device-map-home-inner-html-2">' +
			address1 +
			'<br/>' +
			locality +
			' ' +
			postalCode +
			'</span>';

		serialNum =
			'<a data-automation-id="devices-link" class="device-map-home-inner-html-3" href= "' +
			urlStart +
			'devices/' +
			deviceId +
			'">' +
			'SN: ' +
			serialNum +
			'</a>';

		return (
			locName +
			'<br/>' +
			locAddress +
			'<br/>' +
			(this.isPropertyMarker ? '' : serialNum)
		);
	}

	openClusterMarkerInfo(cluster: any): void {
		this.clusterOfMarkers = cluster.markers;
		const isSamePropertyCluster = this.clusterOfMarkers.every(
			(marker) =>
				marker.getPosition()?.toString() ==
				this.clusterOfMarkers[0].getPosition()?.toString()
		);

		if (isSamePropertyCluster) {
			const markerTitle = this.clusterOfMarkers[this.clusterIndex].getTitle()!;
			this.clusterInfoWindow.setPosition(cluster.position);
			let anchor = new google.maps.MVCObject();
			anchor.set('anchorPoint', { g: true, x: 0, y: -30 });
			this.clusterInfoWindow.open(this.map.googleMap, anchor);

			this.clusterInfoWindowDomReadyListener =
				this.clusterInfoWindow.addListener('domready', () => {
					this.infoWindowBackArrow = document.getElementById('backArrow');
					this.infoWindowBackArrow?.addEventListener(
						'click',
						this.decreaseClusterIndexMethodRef,
						{ passive: true }
					);

					this.infoWindowForwardArrow = document.getElementById('forwardArrow');
					this.infoWindowForwardArrow?.addEventListener(
						'click',
						this.increaseClusterIndexMethodRef,
						{ passive: true }
					);

					this.updateInfoWindowContent();
				});

			this.clusterInfoWindowCloseClickListener =
				this.clusterInfoWindow.addListener('closeclick', () =>
					this.removeClusterOfMarkerListeners()
				);

			if (!this.isMapSubInit) {
				this.isMapSubInit = true;
				this.subscription = this.map.mapClick.subscribe(() => {
					if (!!this.clusterInfoWindow) {
						this.clusterInfoWindow.close();
						this.removeClusterOfMarkerListeners();
					}
				});

				this.subscription = this.map.zoomChanged.subscribe(() => {
					if (!!this.clusterInfoWindow) {
						if (this.currentMapZoom > this.map.getZoom()!) {
							this.clusterInfoWindow.close();
							this.removeClusterOfMarkerListeners();
						}
					}

					if (this.map.getZoom()! <= 22) {
						this.currentMapZoom = this.map.getZoom()!;
					}
				});
			}
		}
	}

	decreaseClusterIndex(): void {
		if (this.clusterIndex > 0) {
			this.clusterIndex--;
		} else {
			this.clusterIndex = this.clusterOfMarkers.length - 1;
		}
		this.updateInfoWindowContent();
	}

	increaseClusterIndex(): void {
		if (this.clusterIndex < this.clusterOfMarkers.length - 1) {
			this.clusterIndex++;
		} else {
			this.clusterIndex = 0;
		}
		this.updateInfoWindowContent();
	}

	updateInfoWindowContent(): void {
		const propertyDeviceInfo = document.getElementById('propertyDeviceInfo')!;
		propertyDeviceInfo.innerHTML = this.createInfoWindowContent(
			this.clusterOfMarkers[this.clusterIndex].getTitle()!
		);
	}

	ngOnDestroy(): void {
		this.presentationSubscription?.unsubscribe();
		this.presentationSubscription = undefined;
	}
}

export interface MarkerInfo {
	marker: google.maps.MarkerOptions;
	deviceSn: string;
	deviceId: string;
	locationName: string;
	address1: string;
	locality: string;
	postalCode: string;
	locationId: string;
}
